import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle, Clock, Eye, Loader2, RefreshCw, ShieldCheck, XCircle, MessageCircle, Mail,
} from "lucide-react";
import { buildLeadWhatsAppUrl } from "@/lib/lead-whatsapp";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminGlassPanel } from "./AdminGlassPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { OnboardingSession, OnboardingStatus } from "@/lib/self-service-provisioning";
import type { OnboardingPaymentAttempt } from "@/lib/onboarding-payment-attempts";
import { clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";

const PAGE_SIZE = 100;

export const ONBOARDING_STATUS_CONFIG: Record<OnboardingStatus, { label: string; color: string; icon: React.ElementType }> = {
  started:           { label: "Iniciado",       color: "bg-gray-100 text-gray-700",    icon: Clock },
  business_info:     { label: "Neg. info",      color: "bg-blue-100 text-blue-700",    icon: Clock },
  account_info:      { label: "Cuenta",         color: "bg-blue-100 text-blue-700",    icon: Clock },
  payment_pending:   { label: "Pago pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  payment_confirmed: { label: "Pago OK",        color: "bg-green-100 text-green-700",  icon: CheckCircle },
  pending_approval:  { label: "Pendiente aprobación", color: "bg-orange-100 text-orange-700", icon: Clock },
  provisioning:      { label: "Provisionando",  color: "bg-cyan-100 text-cyan-700",    icon: Loader2 },
  active:            { label: "Activo ✓",       color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  failed:            { label: "Fallido",        color: "bg-red-100 text-red-700",      icon: XCircle },
  expired:           { label: "Expirado",       color: "bg-gray-100 text-gray-500",    icon: XCircle },
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function usd(cents: number | null, currency?: string | null) {
  if (!cents) return "—";
  const cur = currency ?? "USD";
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: cur }).format(cents / 100);
}

type SessionDetail = {
  session: OnboardingSession;
  paymentAttempts: OnboardingPaymentAttempt[];
  lead: Record<string, unknown> | null;
};

export function OnboardingSessionsPanel({ embedded = false }: { embedded?: boolean } = {}) {
  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState<OnboardingStatus | "all">("all");
  const [acting, setActing] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const buildParams = (offset: number) => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    return params;
  };

  const load = () => {
    setLoading(true);
    fetch(`/api/admin/onboarding?${buildParams(0)}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d: { sessions?: OnboardingSession[] }) => {
        setSessions(d.sessions ?? []);
        setHasMore((d.sessions ?? []).length === PAGE_SIZE);
      })
      .catch(() => toast.error("Error al cargar sesiones"))
      .finally(() => setLoading(false));
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await fetch(`/api/admin/onboarding?${buildParams(sessions.length)}`, { credentials: "include" });
      const d = (await res.json()) as { sessions?: OnboardingSession[] };
      setSessions((prev) => [...prev, ...(d.sessions ?? [])]);
      setHasMore((d.sessions ?? []).length === PAGE_SIZE);
    } catch {
      toast.error("Error al cargar más sesiones");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(load, [statusFilter]);

  const loadDetail = async (sessionId: string) => {
    setDetailId(sessionId);
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/admin/onboarding?sessionId=${encodeURIComponent(sessionId)}`, {
        credentials: "include",
      });
      const data = (await res.json()) as SessionDetail;
      if (!res.ok) throw new Error(String((data as unknown as { error?: string }).error ?? "Error"));
      setDetail(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar detalle");
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const act = async (sessionId: string, action: string) => {
    setActing(sessionId + action);
    try {
      await ensureCsrfToken();
      const res = await fetch("/api/admin/onboarding", {
        method: "POST",
        headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sessionId, action }),
      });
      const data = (await res.json()) as Record<string, unknown>;
      if (!res.ok) throw new Error(String(data.error ?? "Error"));
      toast.success(action === "provision" ? `Tenant ${data.tenantId} provisionado` : "OK");
      load();
      if (detailId === sessionId) void loadDetail(sessionId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const body = (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">{sessions.length} sesiones cargadas</p>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as OnboardingStatus | "all")}>
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {(Object.keys(ONBOARDING_STATUS_CONFIG) as OnboardingStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {ONBOARDING_STATUS_CONFIG[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" /> Actualizar
            </Button>
          </div>
        </div>

        {sessions.length === 0 && (
          <div className="rounded-lg border border-dashed bg-muted/20 py-12 text-center text-sm text-muted-foreground">
            Sin sesiones de onboarding todavía.
          </div>
        )}

        <div className="space-y-2">
          {sessions.map((s) => {
            const cfg = ONBOARDING_STATUS_CONFIG[s.status] ?? ONBOARDING_STATUS_CONFIG.failed;
            const Icon = cfg.icon;
            return (
              <div key={s.id} className="rounded-lg border bg-card p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-foreground truncate">
                        {s.businessName ?? "(sin nombre)"}
                      </p>
                      <Badge className={`text-xs px-1.5 py-0 ${cfg.color} border-0`}>
                        <Icon className="h-3 w-3 mr-1" />
                        {cfg.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {s.ownerName ? `${s.ownerName} · ` : ""}
                      {s.ownerEmail ?? "—"}
                      {s.ownerPhone ? ` · ${s.ownerPhone}` : ""}
                      {s.businessSlug ? ` · /${s.businessSlug}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Plan: <strong>{s.planId}</strong> · {s.billingMode} · {usd(s.amountCents, s.currency)} · {s.paymentProvider ?? "sin proveedor"}
                    </p>
                    {s.errorMessage && (
                      <p className="text-xs text-red-600 mt-1">⚠ {s.errorMessage}</p>
                    )}
                    {s.tenantId && (
                      <p className="text-xs text-emerald-600">✓ Tenant: {s.tenantId}</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground shrink-0">{fmtDate(s.createdAt)}</p>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm" variant="ghost" className="gap-1.5 text-xs h-7"
                    onClick={() => void loadDetail(s.id)}
                  >
                    <Eye className="h-3 w-3" /> Ver detalle
                  </Button>
                  {s.status === "payment_pending" && (
                    <>
                      <Button
                        size="sm" variant="outline" className="gap-1.5 text-xs h-7"
                        disabled={acting === s.id + "confirm_payment"}
                        onClick={() => void act(s.id, "confirm_payment")}
                      >
                        {acting === s.id + "confirm_payment"
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <CheckCircle className="h-3 w-3" />}
                        Confirmar pago
                      </Button>
                      <Button
                        size="sm" variant="outline" className="gap-1.5 text-xs h-7"
                        disabled={acting === s.id + "expire"}
                        onClick={() => void act(s.id, "expire")}
                      >
                        Expirar sesión
                      </Button>
                    </>
                  )}
                  {(s.status === "payment_confirmed" || s.status === "failed") && !s.tenantId && (
                    <Button
                      size="sm" className="gap-1.5 text-xs h-7"
                      disabled={acting === s.id + "provision"}
                      onClick={() => void act(s.id, "provision")}
                    >
                      {acting === s.id + "provision"
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <ShieldCheck className="h-3 w-3" />}
                      Provisionar tenant
                    </Button>
                  )}
                  {s.status === "failed" && (
                    <Button
                      size="sm" variant="outline" className="gap-1.5 text-xs h-7"
                      disabled={acting === s.id + "reset_failed"}
                      onClick={() => void act(s.id, "reset_failed")}
                    >
                      Reintentar
                    </Button>
                  )}
                  {/* Contacto siempre visible cuando hay datos */}
                  {(s.ownerEmail || s.ownerPhone) ? (
                    <>
                      {s.ownerPhone ? (
                        <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" asChild>
                          <a href={buildLeadWhatsAppUrl({ nombre: s.ownerName ?? "", negocio: s.businessName ?? "", email: s.ownerEmail ?? "", telefono: s.ownerPhone, planInteresado: s.planId })} target="_blank" rel="noreferrer">
                            <MessageCircle className="h-3 w-3" /> WhatsApp
                          </a>
                        </Button>
                      ) : null}
                      {s.ownerEmail ? (
                        <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" asChild>
                          <a href={`mailto:${s.ownerEmail}?subject=${encodeURIComponent(`Alta Structura Market — ${s.businessName ?? ""}`)}&body=${encodeURIComponent(`Hola ${s.ownerName ?? ""},\n\nTe escribo por el alta de ${s.businessName ?? "tu tienda"}.\n\nSaludos.`)}`}>
                            <Mail className="h-3 w-3" /> Email
                          </a>
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Cargar más
              </Button>
            </div>
          )}
        </div>
      </div>
  );

  const dialog = (
      <Dialog open={!!detailId} onOpenChange={(o) => { if (!o) { setDetailId(null); setDetail(null); } }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sesión de onboarding</DialogTitle>
            <DialogDescription>Datos cargados, lead vinculado e historial de pagos.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : detail ? (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2">
                {(() => {
                  const cfg = ONBOARDING_STATUS_CONFIG[detail.session.status] ?? ONBOARDING_STATUS_CONFIG.failed;
                  const Icon = cfg.icon;
                  return (
                    <Badge className={`text-xs px-1.5 py-0 ${cfg.color} border-0`}>
                      <Icon className="h-3 w-3 mr-1" />
                      {cfg.label}
                    </Badge>
                  );
                })()}
                <code className="text-[10px] text-muted-foreground">{detail.session.id}</code>
              </div>
              <div className="grid gap-1 sm:grid-cols-2 text-xs">
                <span>Negocio: {detail.session.businessName ?? "—"}</span>
                <span>Contacto: {detail.session.ownerName ?? "—"}</span>
                <span>Email: {detail.session.ownerEmail ?? "—"}</span>
                <span>Tel: {detail.session.ownerPhone ?? "—"}</span>
                <span>Plan: {detail.session.planId}</span>
                <span>Pasarela: {detail.session.paymentProvider ?? "—"}</span>
              </div>
              <div>
                <p className="mb-2 font-medium">Intentos de pago ({detail.paymentAttempts.length})</p>
                {detail.paymentAttempts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin intentos.</p>
                ) : (
                  <div className="max-h-48 space-y-1 overflow-auto rounded border">
                    {detail.paymentAttempts.map((a) => (
                      <div key={a.id} className="flex justify-between border-b px-2 py-1 text-xs last:border-0">
                        <span>{a.provider} · {a.status}{a.errorMessage ? ` — ${a.errorMessage}` : ""}</span>
                        <span>{usd(a.amountCents, a.currency)} · {fmtDate(a.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
  );

  if (embedded) {
    return (
      <>
        {body}
        {dialog}
      </>
    );
  }

  return (
    <AdminGlassPanel
      title="Onboarding sin lead"
      description="Abandonos tempranos de /contratar — todavía no llegaron a cargar sus datos de cuenta. Con lead ya vinculado, se administran desde Leads."
    >
      {body}
      {dialog}
    </AdminGlassPanel>
  );
}
